/**
 * TB303Panel - Interface complète de la TB-303
 * Supporte 2 instances avec accent et slide par step
 * Utilise ThemeManager pour les composants UI
 */

import { themeManager } from '../themes/ThemeManager.js';
import { WaveSelector } from '../components/WaveSelector.js';
import { PresetSelector } from '../components/PresetSelector.js';
import { TB303_PRESETS } from '../../state/InstrumentPresets.js';

export class TB303Panel {
    constructor(tb303, store, sequencer, id = 1) {
        this.tb303 = tb303;
        this.store = store;
        this.sequencer = sequencer;
        this.id = id;

        // Préfixes pour les patterns et IDs HTML
        this.patternPrefix = `bass${id}`;
        this.idPrefix = `tb303_${id}`;
        this.panelId = `tb303_${id}`;

        // Références aux composants UI (pour cleanup)
        this.components = {
            knobs: {},
            stepButtons: { gate: [], accent: [], slide: [] },
            selects: []
        };
    }

    isCollapsed() {
        return this.store?.isPanelCollapsed(this.panelId) || false;
    }

    render(container) {
        this.container = container;
        const isCollapsed = this.isCollapsed();
        const isPowered = this.store.getPowerState(this.idPrefix);

        this.tb303.setPower(isPowered);

        container.innerHTML = `
            <div class="machine tb303 ${isCollapsed ? 'collapsed' : ''} ${!isPowered ? 'powered-off' : ''}" id="${this.idPrefix}_machine">
                <div class="machine-title">
                    <button class="collapse-btn" id="${this.idPrefix}_collapse" title="Réduire/Agrandir">${isCollapsed ? '▶' : '▼'}</button>
                    TB-303 #${this.id} <span class="brand">Bass Line</span>
                    <div id="${this.idPrefix}_presets"></div>
                    <button class="power-btn ${isPowered ? 'on' : ''}" id="${this.idPrefix}_power" title="Power ON/OFF">⏻</button>
                </div>

                <div class="machine-content ${isCollapsed ? 'hidden' : ''}" id="${this.idPrefix}_content">
                <div class="tb303-controls">
                    <div class="tb303-section">
                        <div class="section-title">VCO</div>
                        <div class="controls" id="${this.idPrefix}_vco"></div>
                    </div>
                    <div class="tb303-section vcf-section">
                        <div class="section-title">VCF</div>
                        <div class="controls" id="${this.idPrefix}_vcf"></div>
                    </div>
                    <div class="tb303-section">
                        <div class="section-title">AMP</div>
                        <div class="controls" id="${this.idPrefix}_amp"></div>
                    </div>
                </div>

                <div class="sequencer machine-sequencer">
                    <div class="step-markers">
                        ${Array.from({length: 16}, (_, i) => `<span class="step-marker">${i + 1}</span>`).join('')}
                    </div>
                    <div class="seq-row">
                        <span class="seq-label">GATE</span>
                        <div class="steps" id="${this.idPrefix}_gateSteps"></div>
                    </div>
                    <div class="seq-row">
                        <span class="seq-label">ACCENT</span>
                        <div class="steps accent-steps" id="${this.idPrefix}_accentSteps"></div>
                    </div>
                    <div class="seq-row">
                        <span class="seq-label">SLIDE</span>
                        <div class="steps slide-steps" id="${this.idPrefix}_slideSteps"></div>
                    </div>
                    <div class="seq-row">
                        <span class="seq-label">NOTE</span>
                        <div class="note-row" id="${this.idPrefix}_notes"></div>
                    </div>
                </div>
                </div><!-- /machine-content -->
            </div>
        `;

        this.setupCollapseButton();
        this.setupPowerButton();

        if (!isCollapsed) {
            this.createKnobs();
            this.createGateSteps();
            this.createAccentSteps();
            this.createSlideSteps();
            this.createNoteSelectors();
            this.setupPresetSelector();
        }
    }

    setupCollapseButton() {
        const collapseBtn = document.getElementById(`${this.idPrefix}_collapse`);
        if (!collapseBtn) return;

        collapseBtn.addEventListener('click', () => {
            this.store.togglePanelCollapsed(this.panelId);
            this.destroy();
            this.render(this.container);
        });
    }

    setupPowerButton() {
        const powerBtn = document.getElementById(`${this.idPrefix}_power`);
        const machine = document.getElementById(`${this.idPrefix}_machine`);

        powerBtn.addEventListener('click', () => {
            const isOn = this.tb303.getPower();
            const newState = !isOn;
            this.tb303.setPower(newState);
            this.store.setPowerState(this.idPrefix, newState);

            powerBtn.classList.toggle('on', newState);
            machine.classList.toggle('powered-off', !newState);
        });
    }

    setupPresetSelector() {
        const container = document.getElementById(`${this.idPrefix}_presets`);
        if (!container) return;

        this.presetSelector = new PresetSelector({
            presets: TB303_PRESETS,
            currentPreset: 'Init',
            onChange: (name, data) => this._loadPreset(data)
        });

        container.appendChild(this.presetSelector.element);
    }

    _loadPreset(presetData) {
        if (!presetData) return;

        // Apply preset parameters to synth
        if (presetData.waveform !== undefined) {
            this.tb303.setWaveform(presetData.waveform);
            this.components.waveSelector?.setValue(presetData.waveform);
        }
        if (presetData.cutoff !== undefined) {
            this.tb303.setCutoff(presetData.cutoff);
            this.components.knobs.cutoff?.setValue(presetData.cutoff);
        }
        if (presetData.resonance !== undefined) {
            this.tb303.setResonance(presetData.resonance);
            this.components.knobs.resonance?.setValue(presetData.resonance);
        }
        if (presetData.envMod !== undefined) {
            this.tb303.setEnvMod(presetData.envMod);
            this.components.knobs.envMod?.setValue(presetData.envMod);
        }
        if (presetData.decay !== undefined) {
            this.tb303.setDecay(presetData.decay);
            this.components.knobs.decay?.setValue(presetData.decay);
        }
        if (presetData.accentLevel !== undefined) {
            this.tb303.setAccentLevel(presetData.accentLevel);
            this.components.knobs.accent?.setValue(presetData.accentLevel);
        }
        if (presetData.slideTime !== undefined) {
            this.tb303.setSlideTime(presetData.slideTime);
        }
        if (presetData.distortion !== undefined) {
            this.tb303.setDistortion(presetData.distortion);
            this.components.knobs.distortion?.setValue(presetData.distortion);
        }
        if (presetData.drive !== undefined) {
            this.tb303.setDrive(presetData.drive);
            this.components.knobs.drive?.setValue(presetData.drive);
        }

        // Save to store
        this.store?.setSynthParams(this.idPrefix, presetData);
    }

    createKnobs() {
        const vcoContainer = document.getElementById(`${this.idPrefix}_vco`);
        const vcfContainer = document.getElementById(`${this.idPrefix}_vcf`);
        const ampContainer = document.getElementById(`${this.idPrefix}_amp`);
        const synthId = this.idPrefix;

        // === VCO Section ===
        // Waveform selector avec boutons
        this.components.waveSelector = new WaveSelector(vcoContainer, {
            waveforms: [
                { value: 'sawtooth', label: 'SAW' },
                { value: 'square', label: 'SQR' }
            ],
            value: this.tb303.waveform,
            label: 'Waveform',
            idPrefix: this.idPrefix
        }).onChange(value => {
            this.tb303.setWaveform(value);
            this.store.setSynthParam(synthId, 'waveform', value);
        });

        // === VCF Section ===
        // Cutoff
        this.components.knobs.cutoff = themeManager.createKnob(vcfContainer, {
            min: 100, max: 5000, value: this.tb303.cutoff,
            label: 'Cutoff', unit: 'Hz'
        }).onChange(value => {
            this.tb303.setCutoff(value);
            this.store.setSynthParam(synthId, 'cutoff', value);
        });

        // Resonance
        this.components.knobs.resonance = themeManager.createKnob(vcfContainer, {
            min: 0, max: 30, value: this.tb303.resonance,
            label: 'Resonance'
        }).onChange(value => {
            this.tb303.setResonance(value);
            this.store.setSynthParam(synthId, 'resonance', value);
        });

        // Env Mod
        this.components.knobs.envMod = themeManager.createKnob(vcfContainer, {
            min: 0, max: 5000, value: this.tb303.envMod,
            label: 'Env Mod'
        }).onChange(value => {
            this.tb303.setEnvMod(value);
            this.store.setSynthParam(synthId, 'envMod', value);
        });

        // Decay
        this.components.knobs.decay = themeManager.createKnob(vcfContainer, {
            min: 0.05, max: 0.8, value: this.tb303.decay,
            label: 'Decay', unit: 's', step: 0.01, decimals: 2
        }).onChange(value => {
            this.tb303.setDecay(value);
            this.store.setSynthParam(synthId, 'decay', value);
        });

        // Drive (pre-filter saturation - new in v2.3)
        this.components.knobs.drive = themeManager.createKnob(vcfContainer, {
            min: 0, max: 1, value: this.tb303.drive,
            label: 'Drive', unit: '%', step: 0.01
        }).onChange(value => {
            this.tb303.setDrive(value);
            this.store.setSynthParam(synthId, 'drive', value);
        });

        // === AMP Section ===
        // Accent Level
        this.components.knobs.accentLevel = themeManager.createKnob(ampContainer, {
            min: 0, max: 1, value: this.tb303.accentLevel,
            label: 'Accent Lvl', unit: '%', step: 0.01
        }).onChange(value => {
            this.tb303.setAccentLevel(value);
            this.store.setSynthParam(synthId, 'accentLevel', value);
        });

        // Distortion
        this.components.knobs.distortion = themeManager.createKnob(ampContainer, {
            min: 0, max: 1, value: this.tb303.distortionAmount,
            label: 'Dist', unit: '%', step: 0.01
        }).onChange(value => {
            this.tb303.setDistortion(value);
            this.store.setSynthParam(synthId, 'distortion', value);
        });
    }

    createGateSteps() {
        const container = document.getElementById(`${this.idPrefix}_gateSteps`);
        const patternName = this.patternPrefix;

        for (let i = 0; i < 16; i++) {
            const stepBtn = themeManager.createStepButton(container, {
                index: i,
                type: 'gate',
                active: this.store.patterns[patternName][i]
            }).onChange(isActive => {
                this.store.setStep(patternName, i, isActive);
            });
            this.components.stepButtons.gate.push(stepBtn);
        }
    }

    createAccentSteps() {
        const container = document.getElementById(`${this.idPrefix}_accentSteps`);
        const patternName = `${this.patternPrefix}Accent`;

        for (let i = 0; i < 16; i++) {
            const stepBtn = themeManager.createStepButton(container, {
                index: i,
                type: 'accent',
                active: this.store.patterns[patternName][i]
            }).onChange(isActive => {
                this.store.setStep(patternName, i, isActive);
            });
            this.components.stepButtons.accent.push(stepBtn);
        }
    }

    createSlideSteps() {
        const container = document.getElementById(`${this.idPrefix}_slideSteps`);
        const patternName = `${this.patternPrefix}Slide`;

        for (let i = 0; i < 16; i++) {
            const stepBtn = themeManager.createStepButton(container, {
                index: i,
                type: 'slide',
                active: this.store.patterns[patternName][i]
            }).onChange(isActive => {
                this.store.setStep(patternName, i, isActive);
            });
            this.components.stepButtons.slide.push(stepBtn);
        }
    }

    createNoteSelectors() {
        const container = document.getElementById(`${this.idPrefix}_notes`);
        const patternName = `${this.patternPrefix}Notes`;

        for (let i = 0; i < 16; i++) {
            const select = themeManager.createSelect(container, {
                options: this.store.noteOptions,
                value: this.store.patterns[patternName][i],
                className: 'note-select'
            }).onChange(note => {
                this.store.setStep(patternName, i, note);
            });
            this.components.selects.push(select);
        }
    }

    /**
     * Update knob values from external source (e.g., preset change)
     */
    updateFromParams() {
        const { knobs } = this.components;
        if (knobs.cutoff) knobs.cutoff.setValue(this.tb303.cutoff);
        if (knobs.resonance) knobs.resonance.setValue(this.tb303.resonance);
        if (knobs.envMod) knobs.envMod.setValue(this.tb303.envMod);
        if (knobs.decay) knobs.decay.setValue(this.tb303.decay);
        if (knobs.drive) knobs.drive.setValue(this.tb303.drive);
        if (knobs.accentLevel) knobs.accentLevel.setValue(this.tb303.accentLevel);
        if (knobs.distortion) knobs.distortion.setValue(this.tb303.distortionAmount);

        // Update waveform buttons
        const waveSelector = document.getElementById(`${this.idPrefix}_waveSelector`);
        if (waveSelector) {
            waveSelector.querySelectorAll('.wave-btn').forEach(btn => {
                btn.classList.toggle('active', btn.dataset.wave === this.tb303.waveform);
            });
        }
    }

    /**
     * Cleanup all components
     */
    destroy() {
        Object.values(this.components.knobs).forEach(knob => knob?.destroy());
        this.components.stepButtons.gate.forEach(btn => btn?.destroy());
        this.components.stepButtons.accent.forEach(btn => btn?.destroy());
        this.components.stepButtons.slide.forEach(btn => btn?.destroy());
        this.components.selects.forEach(sel => sel?.destroy());
    }
}
